import { useSyncExternalStore, useEffect } from 'react';

/**
 * Whether physical mailing is on, asked of the server.
 *
 * It used to be two environment variables that had to agree:
 * PHYSICAL_MAILING_DISABLED on the server and a VITE_ copy baked into
 * the client bundle. They drifted, which they were always going to —
 * the client one only changes on a rebuild — and the failure landed at
 * the worst possible moment. The button was live, the person filled in
 * a mailing packet, clicked the last button, and the API answered 503.
 *
 * So there is one variable now, on the server, and the client asks what
 * it says. /api/certified-mail/status already reported it; nothing new
 * is exposed. Flipping it takes effect on the next cold start with no
 * rebuild, and the button can no longer disagree with the route behind
 * it.
 *
 * Fails closed. Until the server answers, mailing reads as off — the
 * same direction the server's own gate fails, and the right one for
 * something that spends postage and cannot be recalled. A rejected or
 * failed status call leaves it off rather than guessing.
 */

let state = { enabled: false, mode: 'off', redirectTo: null, loaded: false };
let inFlight = null;
const listeners = new Set();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The live status object. Identity is stable between changes, which is what useSyncExternalStore needs. */
export function physicalMailingStatus() {
  return state;
}

/** Call-time, so every read gets the current answer rather than one frozen at import. */
export function physicalMailingComingSoon() {
  return !state.enabled;
}

export function setPhysicalMailingStatus(next) {
  const enabled = next?.enabled === true;
  const mode = String(next?.mode || 'off');
  const redirectTo = next?.redirectTo || null;
  if (state.enabled === enabled && state.mode === mode && state.redirectTo === redirectTo && state.loaded) return;
  state = { enabled, mode, redirectTo, loaded: true };
  emit();
}

/** Test seam: forget what the server said, so the next mount asks again. */
export function resetPhysicalMailingStatus() {
  state = { enabled: false, mode: 'off', redirectTo: null, loaded: false };
  inFlight = null;
  emit();
}

/**
 * `fetcher` exists so this module can be exercised without dragging in
 * the browser-only auth chain behind authedFetch, which is also why the
 * real one is imported lazily rather than at the top of the file: a
 * store that decides whether postage gets spent is worth being able to
 * test, and it cannot be if importing it needs a browser.
 */
export async function loadPhysicalMailingStatus(fetcher) {
  // One request per session however many components ask.
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const call = fetcher || (async (url) => {
        const { authedFetch } = await import('../lib/authedFetch.js');
        return authedFetch(url);
      });
      const response = await call('/api/certified-mail/status');
      if (!response?.ok) throw new Error(`HTTP ${response?.status}`);
      setPhysicalMailingStatus(await response.json());
    } catch {
      // Unreachable, unauthorised, or malformed: stay closed. A button
      // that is off when mailing is on is an inconvenience; one that is
      // on when mailing is off spends a click and answers 503.
      setPhysicalMailingStatus({ enabled: false, mode: 'off', redirectTo: null });
    }
    return state;
  })();
  return inFlight;
}

/**
 * Subscribe a component to the status, fetching it the first time
 * anything asks. Returns the whole status, so a screen can also say
 * which PostGrid key is in use and where letters are being redirected.
 */
export function usePhysicalMailingStatus() {
  const status = useSyncExternalStore(subscribe, physicalMailingStatus, physicalMailingStatus);
  useEffect(() => { void loadPhysicalMailingStatus(); }, []);
  return status;
}
